/**
 * Lightweight language guess for showing Translate on About this song.
 * Non-Latin scripts are detected from Unicode ranges. Latin languages use
 * a small stopword scorer so English posts don't get a Translate button
 * for English readers.
 */

const SCRIPT_LANGS = [
  {lang: 'ja', re: /[\u3040-\u30FF]/},
  {lang: 'ko', re: /[\uAC00-\uD7AF]/},
  {lang: 'zh', re: /[\u4E00-\u9FFF]/},
  {lang: 'ar', re: /[\u0600-\u06FF]/},
  {lang: 'he', re: /[\u0590-\u05FF]/},
  {lang: 'th', re: /[\u0E00-\u0E7F]/},
  {lang: 'hi', re: /[\u0900-\u097F]/},
  {lang: 'el', re: /[\u0370-\u03FF]/},
  {lang: 'ru', re: /[\u0400-\u04FF]/},
];

const STOPWORDS = {
  en: [
    'the', 'and', 'you', 'that', 'was', 'for', 'are', 'with', 'this',
    'have', 'but', 'not', 'your', 'from', 'they', 'his', 'her', 'she',
    'been', 'has', 'had', 'were', 'their', 'said', 'one', 'all', 'would',
    'there', 'what', 'when', 'who', 'how', 'about', 'song', 'love',
  ],
  es: [
    'de', 'la', 'el', 'en', 'es', 'un', 'lo', 'se', 'te', 'me', 'mi',
    'su', 'le', 'ya', 'no', 'si', 'qué', 'que', 'los', 'las', 'una',
    'por', 'con', 'para', 'como', 'más', 'está', 'este', 'esta', 'pero',
    'sus', 'del', 'al', 'muy', 'hay', 'también', 'porque', 'cuando',
    'sin', 'sobre', 'canción', 'gusta', 'gustó', 'recomiendo',
  ],
  pt: [
    'não', 'uma', 'para', 'com', 'os', 'as', 'por', 'mais', 'está',
    'como', 'mas', 'seu', 'sua', 'dos', 'das', 'também', 'quando',
    'muito', 'pelo', 'pela', 'isso', 'você',
  ],
  fr: [
    'les', 'des', 'une', 'que', 'est', 'pas', 'pour', 'dans', 'qui',
    'sur', 'avec', 'cette', 'plus', 'sont', 'mais', 'tout', 'nous',
    'vous', 'elle', 'bien', 'aussi', 'comme',
  ],
  de: [
    'und', 'der', 'die', 'das', 'ist', 'nicht', 'ein', 'eine', 'den',
    'dem', 'mit', 'auf', 'für', 'sich', 'von', 'im', 'zu', 'auch',
    'wie', 'aber', 'oder', 'wird', 'sind',
  ],
  it: [
    'che', 'non', 'una', 'per', 'con', 'sono', 'come', 'più', 'della',
    'questo', 'questa', 'ma', 'anche', 'dei', 'nel', 'delle', 'gli',
  ],
  nl: [
    'het', 'een', 'van', 'dat', 'niet', 'met', 'voor', 'zijn', 'er',
    'op', 'te', 'die', 'dit', 'ook', 'als', 'aan',
  ],
  pl: [
    'nie', 'się', 'to', 'jest', 'na', 'że', 'do', 'jak', 'ale', 'od',
    'po', 'za', 'czy', 'tego', 'tej',
  ],
  tr: [
    'bir', 'bu', 've', 'için', 'de', 'da', 'var', 'daha', 'gibi',
    'çok', 'ile', 'ne', 'ama', 'mi',
  ],
  id: [
    'yang', 'dan', 'ini', 'itu', 'dengan', 'untuk', 'tidak', 'dari',
    'pada', 'ada', 'saya', 'kamu',
  ],
  vi: [
    'của', 'và', 'là', 'có', 'không', 'trong', 'một', 'được', 'cho',
    'những', 'với', 'này',
  ],
};

const STOP_SETS = Object.fromEntries(
  Object.entries(STOPWORDS).map(([lang, words]) => [lang, new Set(words)])
);

// Orthography that is rare in English. Used as a score bump so short
// informal posts still classify when they only hit one closed-class word.
const DIACRITIC_BONUS = {
  es: /[ñáéíóúü¿¡]/i,
  pt: /[ãõáéíóúç]/i,
  fr: /[àâæçéèêëîïôœùûüÿ]/i,
  de: /[äöüß]/i,
  it: /[àèéìòù]/i,
  pl: /[ąćęłńóśźż]/i,
  tr: /[çğıöşü]/i,
  vi: /[ăâđêôơưáàảãạ]/i,
};

const LANG_RE = /^([A-Za-z]{2,3})(?:-([A-Za-z]{2,8}))?$/;

/**
 * Normalize a BCP-47 tag to a short Cloud Translation-style code.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeLang(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().replace(/_/g, '-');
  const match = trimmed.match(LANG_RE);
  if (!match) return null;
  const lang = match[1].toLowerCase();
  const region = match[2] ? match[2].toUpperCase() : '';
  if (lang === 'zh') {
    if (region === 'TW' || region === 'HK' || region === 'HANT') return 'zh-TW';
    return 'zh-CN';
  }
  return lang;
}

function detectScriptLang(text) {
  for (const {lang, re} of SCRIPT_LANGS) {
    if (re.test(text)) return lang;
  }
  return null;
}

function detectLatinLang(text) {
  const words = text
    .toLowerCase()
    .replace(/[^\p{L}\s']/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 2);
  if (words.length < 4) return null;

  let best = null;
  let bestScore = 0;
  let second = 0;
  for (const [lang, set] of Object.entries(STOP_SETS)) {
    let score = 0;
    for (const word of words) {
      if (set.has(word)) score += 1;
    }
    if (DIACRITIC_BONUS[lang] && DIACRITIC_BONUS[lang].test(text)) {
      score += 2;
    }
    if (score > bestScore) {
      second = bestScore;
      bestScore = score;
      best = lang;
    } else if (score > second) {
      second = score;
    }
  }
  if (bestScore < 2) return null;
  if (bestScore === second) return null;
  return best;
}

/**
 * Best-effort ISO language for `text`. Returns null when unsure.
 * @param {string} text
 * @returns {string|null}
 */
export function detectLanguage(text) {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const scriptLang = detectScriptLang(trimmed);
  if (scriptLang) return scriptLang;
  return detectLatinLang(trimmed);
}

/**
 * Whether to show a Translate control for this description.
 * @param {string|null} detected
 * @param {string|null} target
 * @param {string} plain
 * @returns {boolean}
 */
export function shouldOfferTranslate(detected, target, plain) {
  if (!plain || plain.trim().length < 8) return false;
  if (!target) return false;
  if (detected) {
    const src = normalizeLang(detected);
    const dst = normalizeLang(target);
    if (!src || !dst) return Boolean(detectScriptLang(plain));
    if (src === dst) return false;
    if (src.split('-')[0] === dst.split('-')[0]) return false;
    return true;
  }
  // Uncertain Latin: don't show Translate to Latin-script readers (avoids
  // a button on every English post). Non-Latin UI languages still get it.
  const dstPrimary = String(target).toLowerCase().split('-')[0];
  const latinReaders = new Set([
    'en', 'es', 'pt', 'fr', 'de', 'it', 'nl', 'pl', 'tr', 'id', 'vi',
    'sv', 'ro', 'da', 'fi', 'cs', 'hu', 'no', 'ca',
  ]);
  if (latinReaders.has(dstPrimary)) return false;
  return plain.trim().length >= 24;
}

/**
 * Human-readable language name in `locale`.
 * @param {string} code
 * @param {string} [locale]
 * @returns {string}
 */
export function languageDisplayName(code, locale = 'en') {
  if (!code) return '';
  try {
    const name = new Intl.DisplayNames([locale], {type: 'language'}).of(
      code
    );
    return name || code;
  } catch {
    return code;
  }
}

export function readerLanguage() {
  if (typeof navigator === 'undefined') return 'en';
  return normalizeLang(navigator.language || navigator.userLanguage) || 'en';
}
